import clone from 'clone';
import { RootSchema } from '../../../union';
import { ISchemaObject, ISchemaObjectProperties, StringKeys } from './object.types';

/**  Types  */
type ObjectSubset<
    Obj extends ISchemaObject<any, string>,
    Keys extends StringKeys<Obj['properties']>
> = ISchemaObject<Pick<Obj['properties'], Keys>, StringKeys<Pick<Obj['properties'], Keys>>>;

type RootSubset<
    Obj extends ISchemaObject<any, string>,
    Keys extends StringKeys<Obj['properties']>
> = RootSchema<
    ISchemaObject<Pick<Obj['properties'], Keys>, StringKeys<Pick<Obj['properties'], Keys>>>
>;

//  Types: pick()
interface PickInputFoundation<Keys extends string> {
    pick: Array<Keys>;
    required: Array<Keys>;
    description: string;
}
interface PickInputRoot<Keys extends string> extends PickInputFoundation<Keys> {
    $id: string;
    title: string;
}
interface PickInputNested<Keys extends string> extends PickInputFoundation<Keys> {
    $id?: undefined;
    title?: undefined;
}
interface PickInputCombo<Keys extends string> extends PickInputFoundation<Keys> {
    $id?: string;
    title?: string;
}

//  Types: omit()
interface OmitInputFoundation<OmittedKeys extends string, Keys extends string> {
    omit: Array<OmittedKeys>;
    required: Array<Exclude<Keys, OmittedKeys>>;
    description: string;
}
interface OmitInputRoot<OmittedKeys extends string, Keys extends string>
    extends OmitInputFoundation<OmittedKeys, Keys> {
    $id: string;
    title: string;
}
interface OmitInputNested<OmittedKeys extends string, Keys extends string>
    extends OmitInputFoundation<OmittedKeys, Keys> {
    $id?: undefined;
    title?: undefined;
}
interface OmitInputCombo<OmittedKeys extends string, Keys extends string>
    extends OmitInputFoundation<OmittedKeys, Keys> {
    $id?: string;
    title?: string;
}

//  Types: append()
type AppendedCombo<
    ExistingProperties extends ISchemaObjectProperties<StringKeys<ExistingProperties>>,
    AppendedProperties extends ISchemaObjectProperties<StringKeys<AppendedProperties>>
> = ISchemaObjectProperties<Extract<keyof ExistingProperties | keyof AppendedProperties, string>>;

interface AppendInputFoundation<
    ExistingProperties extends ISchemaObjectProperties<StringKeys<ExistingProperties>>,
    AppendedProperties extends ISchemaObjectProperties<StringKeys<AppendedProperties>>,
    Properties extends AppendedCombo<ExistingProperties, AppendedProperties>,
    RequiredProperties extends StringKeys<Properties>
> {
    description: string;
    // required: ISchemaObject<Properties, StringKeys<Properties>>['required'];
    required: Array<RequiredProperties>;
    append: ISchemaObject<AppendedProperties, StringKeys<AppendedProperties>>['properties'];
}
interface AppendInputRoot<
    ExistingProperties extends ISchemaObjectProperties<StringKeys<ExistingProperties>>,
    AppendedProperties extends ISchemaObjectProperties<StringKeys<AppendedProperties>>,
    Properties extends AppendedCombo<ExistingProperties, AppendedProperties>,
    RequiredProperties extends StringKeys<Properties>
>
    extends AppendInputFoundation<
        ExistingProperties,
        AppendedProperties,
        Properties,
        RequiredProperties
    > {
    $id: string;
    title: string;
}
interface AppendInputNested<
    ExistingProperties extends ISchemaObjectProperties<StringKeys<ExistingProperties>>,
    AppendedProperties extends ISchemaObjectProperties<StringKeys<AppendedProperties>>,
    Properties extends AppendedCombo<ExistingProperties, AppendedProperties>,
    RequiredProperties extends StringKeys<Properties>
>
    extends AppendInputFoundation<
        ExistingProperties,
        AppendedProperties,
        Properties,
        RequiredProperties
    > {
    $id?: undefined;
    title?: undefined;
}
interface AppendInputCombo<
    ExistingProperties extends ISchemaObjectProperties<StringKeys<ExistingProperties>>,
    AppendedProperties extends ISchemaObjectProperties<StringKeys<AppendedProperties>>,
    Properties extends AppendedCombo<ExistingProperties, AppendedProperties>,
    RequiredProperties extends StringKeys<Properties>
>
    extends AppendInputFoundation<
        ExistingProperties,
        AppendedProperties,
        Properties,
        RequiredProperties
    > {
    $id?: string;
    title?: string;
}

/**
 * @todo
 * - Force new $id, title if dealing with root-level schema
 */

/**
 * Utility: Pick properties from an Object schema
 * @param schema: Object schema
 * @param properties: Properties to pick
 * @param required: Properties to require
 *
 * @todo: Add `required` support
 */
export function pick<
    Obj extends ISchemaObject<any, string>,
    Keys extends StringKeys<Obj['properties']>
>(schema: Obj, input: PickInputRoot<Keys>): RootSubset<Obj, Keys>;
export function pick<
    Obj extends ISchemaObject<any, string>,
    Keys extends StringKeys<Obj['properties']>
>(schema: Obj, input: PickInputNested<Keys>): ObjectSubset<Obj, Keys>;
export function pick<
    Obj extends ISchemaObject<any, string>,
    Keys extends StringKeys<Obj['properties']>
>(
    schema: Obj,
    { pick: properties_to_pick, required, description, $id, title }: PickInputCombo<Keys>
): ObjectSubset<Obj, Keys> {
    const keys = Array.from(new Set([...properties_to_pick]));

    // @ts-expect-error: TS typing issue with subset
    return clone({
        ...schema,

        $id,
        title,
        description,

        required: required,
        properties: pickFromObject(schema.properties, keys),
    });
}

/**
 * Utility: Omit properties from an Object schema
 * @param schema: Object schema
 * @param properties: Properties to pick
 * @param required: Properties to require
 *
 * @todo: Add `required` support
 */
export function omit<
    Obj extends ISchemaObject<any, string>,
    OmittedKeys extends StringKeys<Obj['properties']>
>(
    schema: Obj,
    input: OmitInputRoot<OmittedKeys, StringKeys<Obj['properties']>>
): RootSubset<Obj, Exclude<StringKeys<Obj['properties']>, OmittedKeys>>;
export function omit<
    Obj extends ISchemaObject<any, string>,
    OmittedKeys extends StringKeys<Obj['properties']>
>(
    schema: Obj,
    input: OmitInputNested<OmittedKeys, StringKeys<Obj['properties']>>
): ObjectSubset<Obj, Exclude<StringKeys<Obj['properties']>, OmittedKeys>>;
export function omit<
    Obj extends ISchemaObject<any, string>,
    OmittedKeys extends StringKeys<Obj['properties']>
>(
    schema: Obj,
    {
        omit: properties_to_omit,
        required,
        description,
        $id,
        title,
    }: OmitInputCombo<OmittedKeys, StringKeys<Obj['properties']>>
): ObjectSubset<Obj, Exclude<StringKeys<Obj['properties']>, OmittedKeys>> {
    // @ts-expect-error: TS unimpressed
    return clone({
        ...schema,

        $id,
        title,
        description,

        required: required,
        properties: omitFromObject(schema.properties, properties_to_omit),
    });
}

/**
 * Utility: Append properties to an Object schema
 * @param schema: Object schema
 * @param properties: Properties to append
 * @param required: Properties to require
 */
export function append<
    Obj extends ISchemaObject<any, string>,
    AppendedProperties extends ISchemaObjectProperties<StringKeys<AppendedProperties>>,
    Properties extends AppendedCombo<Obj['properties'], AppendedProperties>,
    RequiredProperties extends StringKeys<Properties>
>(
    schema: Obj,
    input: AppendInputRoot<Obj['properties'], AppendedProperties, Properties, RequiredProperties>
): RootSchema<ISchemaObject<Obj['properties'] & AppendedProperties, RequiredProperties>>;
export function append<
    Obj extends ISchemaObject<any, string>,
    AppendedProperties extends ISchemaObjectProperties<StringKeys<AppendedProperties>>,
    Properties extends AppendedCombo<Obj['properties'], AppendedProperties>,
    RequiredProperties extends StringKeys<Properties>
>(
    schema: Obj,
    input: AppendInputNested<Obj['properties'], AppendedProperties, Properties, RequiredProperties>
): ISchemaObject<Obj['properties'] & AppendedProperties, RequiredProperties>;
export function append<
    Obj extends ISchemaObject<any, string>,
    AppendedProperties extends ISchemaObjectProperties<StringKeys<AppendedProperties>>,
    Properties extends AppendedCombo<Obj['properties'], AppendedProperties>,
    RequiredProperties extends StringKeys<Properties>
>(
    schema: Obj,
    {
        append: properties_to_append,
        required,
        description,
        $id,
        title,
    }: AppendInputCombo<Obj['properties'], AppendedProperties, Properties, RequiredProperties>
): ISchemaObject<Obj['properties'] & AppendedProperties, RequiredProperties> {
    //  Confirm that `properties` typing is good
    const properties = {
        ...schema.properties,
        ...properties_to_append,
    };

    // @ts-expect-error: TS unimpressed
    return clone({
        ...schema,

        $id,
        title,
        description,

        required,
        properties,
    });
}

/**
 * Utility: Omit keys from an object
 * - Like lodash/omit
 */
export function omitFromObject<Obj extends Record<string, any>, KeysToOmit extends keyof Obj>(
    obj: Obj,
    keys_to_omit: Array<KeysToOmit>
): Omit<Obj, KeysToOmit> {
    //  Type-cast, then delete
    const output = clone(obj) as Omit<Obj, KeysToOmit>;
    for (const key of keys_to_omit) {
        // @ts-expect-error: Type-casting above interferes with delete behaviour
        delete output[key];
    }

    return output;
}

/**
 * Utility: Pick keys from an object
 * - Like lodash/pick
 */
export function pickFromObject<Obj extends Record<string, any>, KeysToPick extends keyof Obj>(
    obj: Obj,
    keys_to_pick: Array<KeysToPick>
): Pick<Obj, KeysToPick> {
    //  Type-cast, then add
    const output = {} as Pick<Obj, KeysToPick>;
    for (const key of keys_to_pick) {
        output[key] = obj[key];
    }

    return output;
}
